/**
 * Theme Helpers Module
 *
 * Utility functions for common operations like money formatting, cart management, and DOM manipulation.
 *
 * Usage:
 * import { formatMoneyFromCurrency, getCartStateManager, parseConfig } from '@utilities/theme';
 */

/**
 * Formats an integer amount into a currency string that mirrors Shopify Liquid's
 * `money_without_trailing_zeros` filter output.
 *
 * Uses the store's `shop.money_format` template (exposed via `window.theme.currency.moneyFormat`)
 * to ensure the output exactly matches server-rendered money strings — correct symbol, placement,
 * and spacing for any currency/locale (USD, THB, JPY, SEK, etc.).
 *
 * Falls back to `Intl.NumberFormat` with `narrowSymbol` if no money format is available,
 * then to basic USD formatting as a last resort.
 *
 * @param {number} amountInCents - Amount in cents to format (e.g., 12500 for $125.00)
 * @param {string} [currencyCode='USD'] - ISO 4217 currency code from cart state (e.g., 'USD', 'CAD', 'GBP')
 * @returns {string} Formatted money string (e.g., "$125", "$99.50", "1,000 ฿", "¥125")
 *
 * @example
 * formatMoneyFromCurrency(12500, 'USD'); // "$125"
 * formatMoneyFromCurrency(9950, 'USD');  // "$99.50"
 * formatMoneyFromCurrency(12500, 'THB'); // "125 ฿"
 * formatMoneyFromCurrency(15000, 'JPY'); // "¥150" (zero-decimal currency, rounded)
 */
export function formatMoneyFromCurrency(amountInCents, currencyCode = 'USD') {
  const moneyFormat = window.theme?.currency?.moneyFormat;

  if (moneyFormat) {
    return _formatWithShopifyTemplate(amountInCents, currencyCode, moneyFormat);
  }

  // Fallback: Intl.NumberFormat with narrowSymbol
  return _formatWithIntl(amountInCents, currencyCode);
}

/**
 * Formats an amount using the store's Shopify money format template.
 * Handles zero-decimal currencies (JPY, KRW, etc.) by detecting fraction digits via Intl.
 *
 * @param {number} amountInCents - Amount in cents
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {string} moneyFormat - Shopify money format template (e.g., "${{amount}}", "{{amount}} ฿")
 * @returns {string} Formatted money string
 * @private
 */
function _formatWithShopifyTemplate(amountInCents, currencyCode, moneyFormat) {
  try {
    const amount = amountInCents / 100;

    // Detect zero-decimal currencies (JPY, KRW, VND, etc.) via Intl
    let isZeroDecimal = false;

    try {
      const resolvedOptions = new Intl.NumberFormat('en', {
        style: 'currency',
        currency: currencyCode,
      }).resolvedOptions();
      isZeroDecimal = resolvedOptions.maximumFractionDigits === 0;
    } catch (e) {
      // If Intl fails for this currency, assume standard 2-decimal
    }

    let formatted;

    if (isZeroDecimal) {
      // Zero-decimal currency: round to nearest whole number, no decimals
      const rounded = Math.round(amount);
      formatted = rounded.toLocaleString('en', { maximumFractionDigits: 0 });
    } else {
      // Standard currency: format to 2 decimals, strip trailing .00
      formatted = amount.toFixed(2).replace(/\.00$/, '');
      // Add thousands separators to the integer part
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      formatted = parts.join('.');
    }

    // Replace any Shopify amount placeholder in the format template
    return moneyFormat.replace(/\{\{amount(?:_[^}]*)?\}\}/, formatted);
  } catch (error) {
    // If template formatting fails, try Intl fallback
    return _formatWithIntl(amountInCents, currencyCode);
  }
}

/**
 * Fallback formatter using Intl.NumberFormat with narrowSymbol display.
 *
 * @param {number} amountInCents - Amount in cents
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Formatted money string
 * @private
 */
function _formatWithIntl(amountInCents, currencyCode) {
  const amount = amountInCents / 100;
  const hasFractionalCents = amount % 1 !== 0;

  try {
    return new Intl.NumberFormat(
      document.documentElement.lang || 'en',
      {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: hasFractionalCents ? 2 : 0,
        maximumFractionDigits: hasFractionalCents ? 2 : 0,
      }
    ).format(amount);
  } catch (error) {
    // Last resort: basic USD formatting
    const amt = amountInCents / 100;
    const str = amt % 1 === 0 ? amt.toLocaleString('en') : amt.toFixed(2);
    return `$${str}`;
  }
}

/**
 * Gets the cart state manager instance with consistent fallback pattern
 * @returns {Object|null} CartStateManager instance or null if not available
 */
export function getCartStateManager() {
  return window.cartStateManager || null;
}

/**
 * Gets the single cart drawer element on the page
 * @returns {HTMLElement|null} Cart drawer element or null if not available
 */
export function getCartDrawer() {
  return document.querySelector('cart-drawer');
}

/**
 * Checks if the cart system is ready (state manager available and cart drawer exists)
 * @returns {boolean} True if cart components are available
 */
export function isCartSystemReady() {
  return !!(getCartStateManager() && getCartDrawer());
}

/**
 * Gets the current cart state with fallback to empty state
 * @returns {Object} Current cart state or default empty state
 */
export function getCartState() {
  const stateManager = getCartStateManager();
  return stateManager ? stateManager.getState() : { items: [], item_count: 0, total_price: 0 };
}

/**
 * Safely adds items to cart with proper error handling and flexible options
 * @param {Array|Object} items - Items to add to cart
 * @param {Object|boolean} options - Options object or legacy boolean for openDrawer
 * @param {boolean} options.openDrawer - Whether to open the cart drawer after adding (default: true)
 * @param {boolean} options.refresh - Whether to refresh cart state after adding (default: true)
 * @returns {Promise<Object>} Promise resolving to cart operation result
 * @throws {Error} If cart system is not ready
 */
export async function addToCart(items, options = {}) {
  const stateManager = getCartStateManager();
  if (!stateManager) {
    throw new Error('Cart system is not ready yet. Please try again in a moment.');
  }

  // Handle legacy boolean parameter (backwards compatibility)
  if (typeof options === 'boolean') {
    options = { openDrawer: options };
  }

  // Set defaults
  const { openDrawer = true } = options;

  // Every cart write goes through the standard action, so this wrapper delegates
  // rather than calling the state manager directly. A direct call would mutate the
  // cart without emitting shopify:cart:lines-update.
  return await addToCartViaStandardAction(items, { openDrawer });
}

/**
 * Add item(s) to cart via Shopify's Standard Storefront Action (Shopify.actions.updateCart)
 * instead of the theme's internal addToCart() wrapper.
 *
 * Translates this theme's `{ id, quantity, properties }` line item shape into the
 * standard `{ merchandiseId, quantity, attributes }` line shape Shopify.actions.updateCart
 * expects. `merchandiseId` is plain string templating from the variant id we already
 * have — not a lookup. `properties` become `attributes` as a `{key, value}` array.
 *
 * See: https://shopify.dev/docs/storefronts/themes/best-practices/standard-actions
 *
 * @param {Object|Array} items - Item or array of items ({ id, quantity, properties })
 * @param {Object} [options] - { openDrawer = true, context }
 * @param {string} [options.context] - Standard event `context` for the emitted
 *   shopify:cart:lines-update ('product' | 'cart' | 'dialog'). Omitted, the action
 *   runtime reports 'standard-action', which the spec reserves for automated calls —
 *   so buyer-initiated add flows should pass where the add actually came from.
 * @returns {Promise<{cart: Object, userErrors: Array, warnings: Array, detail: Object}>}
 */
export async function addToCartViaStandardAction(items, options = {}) {
  const { openDrawer = true, context } = options;
  const itemsArray = Array.isArray(items) ? items : [items];

  const lines = itemsArray.map((item) => ({
    merchandiseId: `gid://shopify/ProductVariant/${item.id}`,
    quantity: item.quantity,
    attributes: item.properties
      ? Object.entries(item.properties).map(([key, value]) => ({ key, value }))
      : undefined,
  }));

  return await window.Shopify.actions.updateCart(
    { lines },
    { event: { detail: { openDrawer }, ...(context ? { context } : {}) } }
  );
}

/**
 * Set a cart line's quantity via Shopify's Standard Storefront Action.
 *
 * Routing through the action is what makes `shopify:cart:lines-update` fire, so an
 * app or agent sees a quantity change the same way it sees an add. Calling
 * `cartStateManager.updateItem()` directly mutates the cart but emits no standard
 * event.
 *
 * `lines[].id` carries the theme's own Ajax cart key, which the configured handler
 * resolves. See `#handleStandardUpdateCart` in utilities-cart-state-manager.js.
 *
 * @param {string} key - Ajax cart line key
 * @param {number} quantity - New quantity; pass 0 to remove the line
 * @returns {Promise<{cart: Object, userErrors: Array, warnings: Array, detail: Object}>}
 */
export async function updateCartLineViaStandardAction(key, quantity) {
  return await window.Shopify.actions.updateCart(
    { lines: [{ id: key, quantity }] },
    { event: { context: 'cart' } }
  );
}

/**
 * Remove one or more cart lines via Shopify's Standard Storefront Action.
 *
 * A quantity of 0 removes a line, so this is `updateCartLineViaStandardAction` for
 * a set of keys in a single call.
 *
 * @param {string|string[]} keys - Ajax cart line key, or an array of them
 * @returns {Promise<{cart: Object, userErrors: Array, warnings: Array, detail: Object}>}
 */
export async function removeCartLinesViaStandardAction(keys) {
  const keyArray = Array.isArray(keys) ? keys : [keys];

  return await window.Shopify.actions.updateCart(
    { lines: keyArray.map((key) => ({ id: key, quantity: 0 })) },
    { event: { context: 'cart' } }
  );
}

/**
 * Update cart attributes via Shopify's Standard Storefront Action.
 *
 * Added platform-side 2026-08-06 (see
 * https://shopify.dev/changelog/events-and-actions-cart-attributes-support):
 * `updateCart` now accepts an `attributes` field and auto-emits
 * `shopify:cart:attributes-update` when it changes. Routing through the action
 * is what makes that event fire — calling `cartStateManager.updateAttributes()`
 * directly mutates the cart but emits no standard event, the same tradeoff as
 * `updateCartLineViaStandardAction` vs `updateItem()`.
 *
 * @param {Object} attributes - Plain `{ key: value }` cart attributes object.
 *   Converted here to the `{key, value}[]` shape Shopify.actions.updateCart expects.
 * @returns {Promise<{cart: Object, userErrors: Array, warnings: Array, detail: Object}>}
 */
export async function updateCartAttributesViaStandardAction(attributes) {
  const attributesArray = Object.entries(attributes || {}).map(([key, value]) => ({ key, value }));

  return await window.Shopify.actions.updateCart(
    { attributes: attributesArray },
    { event: { context: 'cart' } }
  );
}

/**
 * Parse configuration from HTML element data attributes
 * @param {HTMLElement} element - Element to parse config from
 * @param {boolean} debug - Whether to enable debug logging
 * @returns {Object} Parsed configuration object
 */
export function parseConfig(element, debug = false) {
  if (!element) {
    if (debug) console.warn('parseConfig: No element provided');
    return {};
  }

  const configScript = element.querySelector(`#${element.id}__json[component-config]`);
  if (debug) {
    console.log(`Config script for ${element.id}:`, configScript);
  }

  let data = {};

  if (configScript) {
    try {
      if (debug) {
        console.log('Config script content:', configScript.textContent);
      }
      data = JSON.parse(configScript.textContent);
      if (debug) {
        console.log('Parsed data:', data);
      }
    } catch (error) {
      console.error('Error parsing component config:', error);
    }
    if (!debug) {
      configScript.remove();
    }
  } else {
    console.warn('No config script found for', element.id);
    return {};
  }
  return data;
}

/**
 * Decode HTML entities in a string
 * @param {string} html - HTML string with entities to decode
 * @returns {string} Decoded string
 */
export function decodeHTMLEntities(html) {
  if (!html || typeof html !== 'string') return html;

  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
}

/**
 * Update or create an element based on the provided configuration
 * @param {HTMLElement} container - Container element to search within
 * @param {Object} update - Update configuration object
 * @param {string} update.selector - CSS selector for the element
 * @param {boolean} update.condition - Whether the element should exist
 * @param {string} [update.content] - Text content to set (optional)
 * @param {string} update.sourceHtml - HTML source containing the element
 * @param {string} update.insertionPoint - CSS selector for insertion point
 * @param {string} update.insertionMethod - Insertion method (afterend, beforeend, etc.)
 */
export function updateOrCreateElement(container, { selector, condition, content, sourceHtml, insertionPoint, insertionMethod }) {
  if (!container) return;

  const existingElement = container.querySelector(selector);

  if (condition) {
    if (existingElement) {
      // Update existing element
      if (content && existingElement.textContent !== content) {
        existingElement.textContent = content;
      }
    } else {
      // Create and insert new element
      createAndInsertElement(container, selector, sourceHtml, insertionPoint, insertionMethod);
    }
  } else if (existingElement) {
    // Remove element if it shouldn't exist
    existingElement.remove();
  }
}

/**
 * Create and insert a new element from HTML source
 * @param {HTMLElement} container - Container element to search within
 * @param {string} selector - CSS selector for the element to create
 * @param {string} sourceHtml - HTML source containing the element
 * @param {string} insertionPoint - CSS selector for insertion point
 * @param {string} insertionMethod - Insertion method
 */
export function createAndInsertElement(container, selector, sourceHtml, insertionPoint, insertionMethod) {
  if (!container || !sourceHtml.includes(selector.replace('.', ''))) return;

  const insertPoint = container.querySelector(insertionPoint);
  if (!insertPoint) return;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = sourceHtml;
  const newElement = tempDiv.querySelector(selector);

  if (newElement) {
    insertPoint.insertAdjacentElement(insertionMethod, newElement);
  }
}

/**
 * Process multiple element updates in batch
 * @param {HTMLElement} container - Container element to search within
 * @param {Array} updates - Array of update configuration objects
 */
export function batchUpdateElements(container, updates) {
  updates.forEach(update => {
    updateOrCreateElement(container, update);
  });
}

/**
 * Create element from HTML string
 * @param {string} htmlString - HTML string to create element from
 * @returns {HTMLElement|null} Created element or null if invalid
 */
export function createElementFromHTML(htmlString) {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString.trim();
  return tempDiv.firstElementChild;
}

/**
 * Check if element matches any of the provided selectors
 * @param {HTMLElement} element - Element to check
 * @param {string|Array<string>} selectors - Selector(s) to match against
 * @returns {boolean} True if element matches any selector
 */
export function matchesAny(element, selectors) {
  if (!element) return false;

  const selectorList = Array.isArray(selectors) ? selectors : [selectors];
  return selectorList.some(selector => element.matches(selector));
}

/**
 * Find the closest ancestor that matches any of the provided selectors
 * @param {HTMLElement} element - Starting element
 * @param {string|Array<string>} selectors - Selector(s) to match against
 * @returns {HTMLElement|null} Matching ancestor or null
 */
export function closestAny(element, selectors) {
  if (!element) return null;

  const selectorList = Array.isArray(selectors) ? selectors : [selectors];
  for (const selector of selectorList) {
    const match = element.closest(selector);
    if (match) return match;
  }
  return null;
}

/**
 * Move child nodes from source elements to target elements
 * @param {string} sourceSelector - CSS selector for source elements
 * @param {string} targetSelector - CSS selector for target elements
 * @param {Object} options - Configuration options
 * @param {boolean} options.removeSource - Whether to remove source elements (default: true)
 */
export function moveChildNodes(sourceSelector, targetSelector, { removeSource = true } = {}) {
  const sourceElements = document.querySelectorAll(sourceSelector);
  const targetElements = document.querySelectorAll(targetSelector);

  if (!(sourceElements?.length > 0 && targetElements?.length > 0)) {
    if (!targetElements?.length) {
      console.warn(`No target elements found for '${targetSelector}'`);
    }
    if (!sourceElements?.length) {
      console.warn(`No source elements found for '${sourceSelector}'`);
    } else {
      // We have source elements but missing target elements - remove source elements
      sourceElements.forEach((sourceElement) => sourceElement.remove());
    }
    return;
  }

  // Collect all child nodes from all source elements
  const allChildNodes = [];
  sourceElements.forEach((sourceElement) => {
    allChildNodes.push(...sourceElement.childNodes);
  });

  // For each target, append clones of all child nodes
  targetElements.forEach((targetElement) => {
    const fragment = document.createDocumentFragment();

    allChildNodes.forEach((childNode) => {
      fragment.appendChild(childNode.cloneNode(true));
    });

    targetElement.appendChild(fragment);
  });

  // Remove original source elements if specified
  if (removeSource) {
    sourceElements.forEach((sourceElement) => sourceElement.remove());
  }
}

/**
 * Determine if the current device is a mobile device, or at least within a mobile viewport size.
 * @returns {boolean} True if the device is a mobile device, or at least within a mobile viewport size.
 */
export function isMobile() {
  return window.innerWidth < (window.theme?.breakpoints?.desktop || 768);
}

/**
 * Converts a string to a boolean
 * @param {string} str - The string to convert
 * @returns {boolean|null} The converted boolean or null if invalid
 */
export function stringToBoolean(str) {
  if (typeof str !== 'string') return null;
  return str.toLowerCase() === 'true' ? true : str.toLowerCase() === 'false' ? false : null;
}

/**
 * Converts a string to a handle
 * @param {string} text - The string to convert
 * @returns {string} The converted handle
 */
export function handleize(text) {
  return text?.toLowerCase().trim().replace(/[^\w\s]/g, "").replace(/\s+/g, "-");
}

/**
 * Capitalizes the first character of each word chunk split by various delimiters
 * @param {string} text - The string to capitalize
 * @returns {string} The capitalized string
 */
export function capitalize(text) {
  if (!text) return '';
  const regex = /(\w+|[\s\/\\\-\_]+)/g;
  return text.match(regex).map(part => {
    if (/\w/.test(part)) {
      return part[0].toUpperCase() + part.substring(1).toLowerCase();
    }
    return part;
  }).join('');
}

/**
 * Check if multiple arrays have the same values regardless of order
 * @param {...Array} arrays - Two or more arrays to compare
 * @returns {boolean} True if all arrays contain the same values
 */
export function arraysEqual(...arrays) {
  // Must have at least 2 arrays
  if (arrays.length < 2) return false;

  // All arrays must have the same length
  const firstLength = arrays[0].length;
  if (!arrays.every(arr => arr.length === firstLength)) return false;

  // Sort the first array as reference
  const sortedFirst = [...arrays[0]].sort();

  // Compare all other arrays against the first
  return arrays.slice(1).every(arr => {
    const sorted = [...arr].sort();
    return sorted.every((value, index) => value === sortedFirst[index]);
  });
}

/**
 * Check if an array is empty
 * @param {Array} array - The array to check
 * @returns {boolean} True if the array is empty
 */
export function isEmptyArray(array) {
  return Array.isArray(array) && array.length === 0;
}

/**
 * Updates the document title
 * @param {string} newTitle - The new title to set
 */
export function updateDocumentTitle(newTitle) {
  if (typeof newTitle === 'string' && newTitle.trim()) {
    document.title = newTitle;
  }
}

/**
 * Adds or removes the dark-mode class from the body element
 * @param {boolean} bool - The boolean value to determine if the dark-mode class should be added or removed
 */
export function darkMode(bool) {
  if (bool) {
    document.querySelector('body').classList.add('dark-mode');
  } else {
    document.querySelector('body').classList.remove('dark-mode');
  }
}

window.darkMode = darkMode;

/**
 * Parse URL parameters to extract active filters
 * @param {string|null} urlParameters - URL search parameters string, or null to use current location
 * @param {Object} options - Configuration options
 * @param {boolean} options.debug - Whether to enable debug logging
 * @param {Array<string>} options.availabilityFilters - Array of availability filter parameter names
 * @returns {Object} Parsed active filters object
 */
export function parseUrlParametersToActiveFilters(urlParameters = null, options = {}) {
  const { debug = false, availabilityFilters = ['filter.p.m.greyson.availability', 'filter.v.m.greyson.availability'] } = options;

  const activeFilters = {};
  const params = new URLSearchParams(urlParameters || window.location.search);

  for (const [key, value] of params.entries()) {
    if (key.startsWith("filter.")) {
      if (!activeFilters[key]) {
        activeFilters[key] = [];
      }
      activeFilters[key].push(value);

      // Log availability filters if debug is enabled
      if (availabilityFilters.includes(key) && debug) {
        console.log(`Availability filter applied: ${key}=${value}`);
      }
    }
  }

  if (debug) {
    console.log("Parsed activeFilters from URL:", activeFilters);
  }

  return activeFilters;
}

/**
 * Check if an element is currently in the viewport
 * @param {HTMLElement} element - Element to check
 * @param {Object} options - Configuration options
 * @param {number} options.margin - Additional margin in pixels to consider (default: 0)
 * @returns {boolean} True if element is in viewport
 */
export function isInViewport(element, options = {}) {
  if (!element) return false;

  const { margin = 0 } = options;
  const rect = element.getBoundingClientRect();

  return (
    rect.top < window.innerHeight + margin &&
    rect.bottom > -margin &&
    rect.left < window.innerWidth + margin &&
    rect.right > -margin
  );
}

/**
 * Cache of lazy load observers keyed by configuration
 * @type {Map<string, IntersectionObserver>}
 */
const _lazyLoadComponentObservers = new Map();

/**
 * Generate a cache key from observer options
 * @param {Object} options - Observer options
 * @returns {string} Cache key
 * @private
 */
function getObserverCacheKey(options) {
  const threshold = Array.isArray(options.threshold)
    ? options.threshold.join(',')
    : String(options.threshold);
  return `${threshold}|${options.rootMargin || '0px'}`;
}

/**
 * Observe an element for lazy loading, handling elements already in viewport
 * @param {HTMLElement} element - Element to observe
 * @param {Object} observerOptions - Observer configuration options
 * @param {number|Array<number>} [observerOptions.threshold=0] - Threshold(s) for intersection
 * @param {string} [observerOptions.rootMargin='50px'] - Root margin for intersection detection
 * @returns {Promise<void>} Promise that resolves when element is initialized (if already in viewport) or when observation starts
 */
export async function observeLazyLoadComponent(element, observerOptions = {}) {
  if (!element) return;

  // Check if element is already in viewport before observing
  // IntersectionObserver only fires on state changes, not initial state
  const margin = observerOptions.rootMargin
    ? parseInt(observerOptions.rootMargin.replace('px', '')) || 50
    : 50;

  if (isInViewport(element, { margin })) {
    // Element is already visible, initialize immediately
    element.classList.remove("lazy-load");
    if (element.init) {
      await element.init();
    }
    return;
  }

  // Element not yet visible, observe it
  const observer = getLazyLoadComponentObserver(observerOptions);
  observer.observe(element);
}

/**
 * Get or create a lazy load component observer with optional custom configuration
 * Observers are cached by configuration (threshold + rootMargin) to avoid creating duplicates
 * @param {Object} options - Observer configuration options
 * @param {number|Array<number>} [options.threshold=0] - Threshold(s) for intersection (0-1 or array)
 *   Use 0 for variable-sized elements (triggers when any part is visible)
 *   Use higher values (0.25, 0.5) for fixed-sized elements
 * @param {string} [options.rootMargin='50px'] - Root margin for intersection detection
 * @returns {IntersectionObserver} IntersectionObserver instance
 */
export function getLazyLoadComponentObserver(options = {}) {
  // Default options
  // threshold: 0 means "trigger when any part enters viewport" - better for variable-sized elements
  const defaultOptions = {
    threshold: 0,
    rootMargin: '50px'
  };

  // Merge with provided options
  const observerOptions = {
    threshold: options.threshold !== undefined ? options.threshold : defaultOptions.threshold,
    rootMargin: options.rootMargin !== undefined ? options.rootMargin : defaultOptions.rootMargin
  };

  // Generate cache key
  const cacheKey = getObserverCacheKey(observerOptions);

  // Return cached observer or create new one
  if (!_lazyLoadComponentObservers.has(cacheKey)) {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(async (entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("lazy-load");
            observer.unobserve(entry.target);
            // the component must have an init method
            if (!entry.target.init) {
              console.error('Component does not have an init method:', entry.target);
              return;
            }
            await entry.target.init();
          }
        });
      },
      observerOptions
    );
    _lazyLoadComponentObservers.set(cacheKey, observer);
  }

  return _lazyLoadComponentObservers.get(cacheKey);
}

/**
 * Extract and inject script tags from container to trigger execution
 * When HTML is injected via innerHTML or DOM methods, script tags don't execute.
 * This function finds all script tags, recreates them to trigger execution, and
 * optionally tracks scripts with IDs for later cleanup. Prevents duplicate injections
 * by checking if a script with the same ID or src already exists in the DOM.
 * @param {HTMLElement} container - Container element to search for scripts
 * @param {Set} [injectedScriptsTracker] - Optional Set to track script IDs for cleanup
 * @param {Object} options - Configuration options
 * @param {boolean} options.debug - Whether to enable debug logging
 * @returns {void}
 */
export function extractAndInjectScripts(container, injectedScriptsTracker = null, options = {}) {
  if (!container) return;

  const { debug = false } = options;
  const scripts = container.querySelectorAll('script');

  for (const oldScript of scripts) {
    const scriptId = oldScript.id;
    const scriptSrc = oldScript.src;
    const scriptType = oldScript.getAttribute('type');

    // Skip JSON config scripts - they don't need re-execution and are already in the DOM
    // from the initial DOM insertion. Re-processing them would incorrectly remove them.
    if (scriptType === 'application/json') {
      if (debug) {
        console.log(`Skipping JSON config script: ${scriptId || '(no id)'}`);
      }
      continue;
    }

    // Check if script already exists in DOM by ID
    if (scriptId && document.getElementById(scriptId)) {
      if (debug) {
        console.log(`Script with ID "${scriptId}" already exists in DOM, skipping injection`);
      }
      oldScript.remove();
      continue;
    }

    // Check if script already exists in DOM by src (for external scripts)
    if (scriptSrc && document.querySelector(`script[src="${scriptSrc}"]`)) {
      if (debug) {
        console.log(`Script with src "${scriptSrc}" already exists in DOM, skipping injection`);
      }
      oldScript.remove();
      continue;
    }

    // Script doesn't exist yet, so inject it
    const newScript = document.createElement('script');

    for (const attr of oldScript.attributes) {
      newScript.setAttribute(attr.name, attr.value);
    }

    if (oldScript.textContent) {
      newScript.textContent = oldScript.textContent;
    }

    oldScript.parentNode.replaceChild(newScript, oldScript);

    // Track scripts with IDs if a tracker is provided
    if (injectedScriptsTracker && scriptId) {
      injectedScriptsTracker.add(scriptId);
      if (debug) {
        console.log(`Injected script tag with ID: ${scriptId}`);
      }
    } else if (debug) {
      if (scriptSrc) {
        console.log(`Injected script tag with src: ${scriptSrc} (untracked)`);
      } else {
        console.log(`Injected inline script tag (untracked)`);
      }
    }
  }
}

/**
 * Extract and inject style tags and stylesheet links from container into document head
 * Tracks injected styles with IDs to avoid duplicates. Styles without IDs are
 * still injected but not tracked (and thus cannot be cleaned up). Prevents duplicate
 * injections by checking if a style with the same ID already exists in the DOM, or if
 * a stylesheet link with the same href already exists.
 * @param {HTMLElement} container - Container element to search for styles
 * @param {Set} [injectedStylesTracker] - Optional Set to track which style IDs have been injected
 * @param {Object} options - Configuration options
 * @param {boolean} options.debug - Whether to enable debug logging
 * @returns {void}
 */
export function extractAndInjectStyles(container, injectedStylesTracker = null, options = {}) {
  if (!container) return;

  const { debug = false } = options;

  // Process inline style tags
  const styleTags = container.querySelectorAll("style");
  styleTags.forEach((styleTag) => {
    const styleId = styleTag.id;

    // Check if style has ID and if it's already in the DOM
    if (styleId && document.getElementById(styleId)) {
      if (debug) {
        console.log(`Style with ID "${styleId}" already exists in DOM, skipping injection`);
      }
      styleTag.remove();
      return;
    }

    // Check if style has ID and if it's already been tracked
    if (styleId && injectedStylesTracker && injectedStylesTracker.has(styleId)) {
      if (debug) {
        console.log(`Style with ID "${styleId}" already tracked, skipping injection`);
      }
      styleTag.remove();
      return;
    }

    // Clone the style tag to inject into document head
    const styleClone = styleTag.cloneNode(true);

    // Inject into document head
    document.head.appendChild(styleClone);

    // Track style if it has an ID
    if (styleId && injectedStylesTracker) {
      injectedStylesTracker.add(styleId);
      if (debug) {
        console.log(`Injected style tag with ID: ${styleId}`);
      }
    } else if (debug && !styleId) {
      console.log(`Injected style tag without ID (untracked)`);
    }

    // Remove the style tag from the container since it's now in the head
    styleTag.remove();
  });

  // Process stylesheet link tags
  const linkTags = container.querySelectorAll("link[rel='stylesheet']");
  linkTags.forEach((linkTag) => {
    const linkId = linkTag.id;
    const linkHref = linkTag.href;

    // Check if link has ID and if it's already in the DOM
    if (linkId && document.getElementById(linkId)) {
      if (debug) {
        console.log(`Link with ID "${linkId}" already exists in DOM, skipping injection`);
      }
      linkTag.remove();
      return;
    }

    // Check if link with same href already exists in document head
    if (linkHref && document.querySelector(`link[rel='stylesheet'][href="${linkHref}"]`)) {
      if (debug) {
        console.log(`Link with href "${linkHref}" already exists in DOM, skipping injection`);
      }
      linkTag.remove();
      return;
    }

    // Check if link has ID and if it's already been tracked
    if (linkId && injectedStylesTracker && injectedStylesTracker.has(linkId)) {
      if (debug) {
        console.log(`Link with ID "${linkId}" already tracked, skipping injection`);
      }
      linkTag.remove();
      return;
    }

    // Clone the link tag to inject into document head
    const linkClone = linkTag.cloneNode(true);

    // Inject into document head
    document.head.appendChild(linkClone);

    // Track link if it has an ID
    if (linkId && injectedStylesTracker) {
      injectedStylesTracker.add(linkId);
      if (debug) {
        console.log(`Injected link tag with ID: ${linkId}`);
      }
    } else if (debug) {
      if (linkHref) {
        console.log(`Injected link tag with href: ${linkHref} (untracked)`);
      } else {
        console.log(`Injected link tag without href (untracked)`);
      }
    }

    // Remove the link tag from the container since it's now in the head
    linkTag.remove();
  });
}

/**
 * Clean content block element by removing any remaining style and link tags
 * @param {HTMLElement} container - Container element to search for styles
 * @returns {HTMLElement} - Cleaned element
 */
export function cleanContentBlockElement(container) {
  if (!container) return container;

  // Remove any style tags that might still be in the content block
  const styleTags = container.querySelectorAll("style");
  styleTags.forEach((styleTag) => styleTag.remove());

  // Remove any stylesheet link tags that might still be in the content block
  const linkTags = container.querySelectorAll("link[rel='stylesheet']");
  linkTags.forEach((linkTag) => linkTag.remove());

  return container;
}

/**
 * Clean up injected styles by removing them from document head
 * @param {Set} injectedStylesTracker - Set of style IDs to remove
 * @param {Object} options - Configuration options
 * @param {boolean} options.debug - Whether to enable debug logging
 * @returns {void}
 */
export function cleanupInjectedStyles(injectedStylesTracker, options = {}) {
  if (!injectedStylesTracker) return;

  const { debug = false } = options;

  // Remove all styles that were injected
  injectedStylesTracker.forEach((styleId) => {
    const styleElement = document.head.querySelector(`style#${styleId}`);
    if (styleElement) {
      styleElement.remove();
      if (debug) {
        console.log(`Removed injected style tag with ID: ${styleId}`);
      }
    }
  });

  // Clear the tracking set
  injectedStylesTracker.clear();
}

/**
 * Clean up injected scripts by removing them from the container
 * @param {Set} injectedScriptsTracker - Set of script IDs to remove
 * @param {Object} options - Configuration options
 * @param {boolean} options.debug - Whether to enable debug logging
 * @returns {void}
 */
export function cleanupInjectedScripts(injectedScriptsTracker, options = {}) {
  if (!injectedScriptsTracker) return;

  const { debug = false } = options;

  // Remove all scripts that were injected
  injectedScriptsTracker.forEach((scriptId) => {
    const scriptElement = document.querySelector(`script#${scriptId}`);
    if (scriptElement) {
      scriptElement.remove();
      if (debug) {
        console.log(`Removed injected script tag with ID: ${scriptId}`);
      }
    }
  });

  // Clear the tracking set
  injectedScriptsTracker.clear();
}

/**
 * Creates a skeleton product card element for loading states
 * Mimics the structure of an actual product card with placeholder elements
 * @returns {HTMLElement} A skeleton product card DOM element
 */
export function createSkeletonProductCard() {
  const fragment = document.createDocumentFragment();
  const inner = document.createElement('div');
  inner.className = 'product-card__inner';
  inner.innerHTML = `
    <div class="product-card-skeleton__media">
      <div class="product-card-skeleton__media-image"></div>
    </div>
  `;
  const content = document.createElement('div');
  content.className = 'product-card__content';
  content.innerHTML = `
    <div class="product-card__info product-card-skeleton__info">
      <div class="product-card-skeleton__title"></div>
      <div class="product-card-skeleton__subtitle"></div>
      <div class="product-card-skeleton__price"></div>
    </div>
    <div class="product-card-skeleton__swatches-row">
      <span class="product-card-skeleton__swatch"></span>
      <span class="product-card-skeleton__swatch"></span>
      <span class="product-card-skeleton__swatch"></span>
    </div>
  `;
  fragment.appendChild(inner);
  fragment.appendChild(content);
  return fragment;
}

/**
 * Creates a container with multiple skeleton product cards for loading states
 * Default number of cards matches typical grid view
 * @param {number} count - Number of skeleton cards to generate (default: 4)
 * @returns {HTMLElement} A container element with skeleton cards
 */
export function createSkeletonProductGrid(count = 4) {
  const container = document.createElement('div');
  container.className = 'product-grid';
  container.setAttribute('data-products-container', '');

  for (let i = 0; i < count; i++) {
    const skeletonCard = createSkeletonProductCard();
    container.appendChild(skeletonCard);
  }

  return container;
}

/* -------------------------------------------------------------------------- *
 * Timing helpers
 * -------------------------------------------------------------------------- */

/**
 * Creates a debounced version of a function that delays invoking until after
 * `wait` milliseconds have elapsed since the last time it was invoked.
 * @param {Function} fn - The function to debounce
 * @param {number} wait - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Creates a throttled version of a function that only invokes at most once per
 * every `limit` milliseconds.
 * @param {Function} fn - The function to throttle
 * @param {number} limit - Minimum time between invocations in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(fn, limit) {
  let lastFunc;
  let lastRan;
  return () => {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      fn.apply(context, args);
      lastRan = Date.now()
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if ((Date.now() - lastRan) >= limit) {
          fn.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan))
    }
  }
}

/**
 * Builds a standard Shopify AJAX fetch config object for POST requests.
 * @param {string} [type='json'] - The Accept content subtype
 * @returns {Object} Fetch init object with method and headers
 */
export function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

/* -------------------------------------------------------------------------- *
 * Flickity readiness
 * -------------------------------------------------------------------------- */

/**
 * Runs a callback once Flickity is available on the page, polling and listening
 * for the `flickity:loaded` event. Returns a cleanup function that cancels the
 * wait if called before Flickity loads.
 * @param {Function} onReady - Callback invoked when Flickity is ready
 * @param {Object} [options] - Configuration options
 * @param {number} [options.intervalMs=250] - Poll interval in milliseconds
 * @param {number} [options.maxAttempts=80] - Maximum poll attempts before giving up
 * @returns {Function} Cleanup function
 */
export function waitForFlickity(onReady, options = {}) {
  if (typeof onReady !== 'function') return () => {};

  const intervalMs = Number(options.intervalMs || 250);
  const maxAttempts = Number(options.maxAttempts || 80);
  let attempts = 0;
  let isCleaned = false;
  let pollInterval = null;

  const cleanup = () => {
    if (isCleaned) return;
    isCleaned = true;
    if (pollInterval) {
      window.clearInterval(pollInterval);
      pollInterval = null;
    }
    document.removeEventListener('flickity:loaded', handleReady);
  };

  const handleReady = () => {
    if (!window.Flickity) return;
    cleanup();
    onReady();
  };

  if (window.Flickity || window.__flickityReady) {
    onReady();
    return cleanup;
  }

  document.addEventListener('flickity:loaded', handleReady);

  pollInterval = window.setInterval(() => {
    attempts += 1;
    if (window.Flickity) {
      handleReady();
      return;
    }

    if (attempts >= maxAttempts) {
      cleanup();
    }
  }, intervalMs);

  return cleanup;
}

/* -------------------------------------------------------------------------- *
 * Focus management
 * -------------------------------------------------------------------------- */

/**
 * Returns all focusable elements within a container, in document order.
 * @param {HTMLElement} container - Container to search within
 * @returns {Array<HTMLElement>} Focusable elements
 */
export function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  // Hidden matches (display: none ancestors, e.g. a mobile-only input on
  // desktop) are real DOM matches but not real Tab stops — focusing one is a
  // silent no-op, which made trapFocus() appear to dead-end when its
  // computed `first`/`last` landed on one.
  ).filter((element) => element.offsetParent !== null)
  // A closed <details>'s content keeps a non-null offsetParent (native
  // <details> hides it via ::details-content/content-visibility, which
  // doesn't detach layout the way display:none does), so the check above
  // alone doesn't exclude it. Left in, a nested closed accordion's own
  // links get counted as real Tab stops even though the browser's actual
  // Tab order skips straight past them — trapFocus's computed `last` then
  // points at content that's never really reached, so Tab-from-the-true-
  // last-item never matches it and escapes the trap entirely.
  //
  // <summary> is exempt: it's always the visible toggle for its own
  // <details> regardless of that details' open state, so only *other*
  // elements' closed-<details> ancestors count as hiding them.
  .filter((element) => {
    if (element.tagName === 'SUMMARY') return true;
    return !element.closest('details:not([open])');
  });
}

/**
 * Shared handler references for the active focus trap. Module-scoped so
 * trapFocus/removeTrapFocus operate on a single trap at a time.
 * @type {Object}
 * @private
 */
const trapFocusHandlers = {};

/**
 * Traps keyboard focus within a container, cycling Tab/Shift+Tab between the
 * first and last focusable elements. Removes any previously active trap first.
 * @param {HTMLElement} container - Container to trap focus within
 * @param {HTMLElement} [elementToFocus=container] - Element to focus initially
 */
export function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();

  // Recomputed on every keydown rather than cached once here: containers like
  // the predictive search modal render their content (suggestions, then
  // products) asynchronously after the trap is set up, so a one-time
  // snapshot's `last` element goes stale — Tab from the real last element
  // (now past the stale one) matches neither the cached first nor last and
  // falls through with no focus change, appearing to dead-end.
  trapFocusHandlers.focusin = (event) => {
    if (!container.contains(event.target) && event.target !== container) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key

    const elements = getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus) elementToFocus.focus();
}

/**
 * Removes the active focus trap, optionally returning focus to an element.
 * @param {HTMLElement} [elementToFocus=null] - Element to focus after releasing the trap
 */
export function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

/**
 * Polyfills `:focus-visible` behavior for browsers that don't support the
 * selector, adding a `focused` class on keyboard focus only.
 */
export function focusVisiblePolyfill() {
  const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if(navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

    if (mouseClick) return;

    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('focused');

  }, true);
}

// Run the focus-visible polyfill if the browser lacks native support.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

/* -------------------------------------------------------------------------- *
 * Media control
 * -------------------------------------------------------------------------- */

/**
 * Pauses all media on the page: YouTube/Vimeo iframes, native videos, and
 * Shopify product models.
 */
export function pauseAllMedia() {
  // Use specific origins for security instead of '*'
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', 'https://www.youtube.com');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', 'https://player.vimeo.com');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

/* -------------------------------------------------------------------------- *
 * Body scroll lock
 * -------------------------------------------------------------------------- */

/**
 * Locks/unlocks body scrolling while preserving scroll position. Used by drawers
 * and modals to prevent background scroll. A single shared instance is exported
 * as `scrollLock`.
 */
export class ScrollLock {
  constructor() {
    this.mobileScrollPosition = 0;
    this.mobileTopLogo = document.querySelector('.dynamic-logo__mobile');
    this.mobileScrollLogo = document.querySelector('.dynamic-logo__change');
    this.preventScrollEvents = false;
    this.scrollEventPreventer = this.scrollEventPreventer.bind(this);
  }

  enabledBodyScrollLock(bodyPos = null) {
    this.mobileScrollPosition = window.pageYOffset;
    let pos = this.mobileScrollPosition;
    if(bodyPos) pos = bodyPos;

    if(pos > 1 && this.mobileTopLogo && this.mobileScrollLogo) {
      this.mobileTopLogo.style.opacity = 0;
      this.mobileScrollLogo.style.opacity = 1;
    }

    // Prevent scroll event from firing when we set position: fixed; on body. We don't want other elements reacting to this "scroll" event
    this.preventScrollEvents = true;
    window.addEventListener('scroll', this.scrollEventPreventer, { capture: true, passive: false });

    // Apply body styles to prevent body scroll interaction
    const body = document.querySelector('body');
    if (body) {
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${pos}px`;
      body.style.width = '100%';
    }
  }

  /**
   * @param {boolean} [restoreScroll=true] - Whether to scroll back to the
   *   position captured by enabledBodyScrollLock(). A caller that already
   *   moved the page elsewhere while the lock was active (e.g. a filter
   *   drawer whose applied filter/sort scrolled the grid to the top, per
   *   GC-2437) should pass false so this restore doesn't undo that.
   */
  disableBodyScrollLock(restoreScroll = true) {
    if(this.mobileTopLogo && this.mobileScrollLogo)  {
      this.mobileTopLogo.style.removeProperty('opacity');
      this.mobileScrollLogo.style.removeProperty('opacity');
    }

    const body = document.querySelector('body');
    if (body) ['overflow', 'position', 'top', 'width'].forEach(prop => body.style.removeProperty(prop));

    // Remove scroll event prevention
    this.preventScrollEvents = false;
    window.removeEventListener('scroll', this.scrollEventPreventer, { capture: true, passive: false });

    if (restoreScroll) {
      // Return to where we had scrolled before locking the body
      window.scrollTo({top: this.mobileScrollPosition, left: 0, behavior: 'instant' });
    }
  }

  scrollEventPreventer(event) {
    if (this.preventScrollEvents) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }
}

/**
 * Shared ScrollLock instance for the theme.
 * @type {ScrollLock}
 */
export const scrollLock = new ScrollLock();

/**
 * Format an enhanced Nosto error into a single compact, screenshot-friendly line.
 *
 * Lives here (not utilities-nosto.js) because it's called from vendor-agnostic
 * error handlers (filter-drawer.js, product-grid.js, predictive-search.js) that
 * must not pull in the full Nosto utilities module just to format an error line.
 * Pure formatter with no Nosto dependency - reads metadata attached by Nosto's
 * error-creation helper, and returns '' for any non-Nosto error.
 *
 * Example output: `NOSTO·LIB_TIMEOUT · 20034ms · Nosto library failed to load within timeout`
 *
 * @param {Error} error - An error, possibly enhanced with Nosto error metadata
 * @returns {string} A compact diagnostic string, or '' when not a Nosto error
 */
export function formatNostoErrorCode(error) {
  if (!error?.code) return '';

  const stem = error.stem || String(error.code).replace(/^NOSTO_WRAPPER_/, '');
  const parts = [`NOSTO·${stem}`];

  const details = error.details || {};

  if (Number.isFinite(details.elapsed)) {
    parts.push(`${Math.round(details.elapsed)}ms`);
  }
  if (details.trigger) {
    parts.push(String(details.trigger));
  }

  if (error.debugMessage) {
    parts.push(String(error.debugMessage));
  }

  return parts.join(' · ');
}


/**
 * Promotional pricing (GC-2442)
 *
 * A `product_promotion` metaobject can carry a discount the theme itself applies.
 * These helpers turn that promotion into a post-discount price, so the PDP and the
 * product card show the price the customer actually pays.
 *
 * Only automatic discounts render. A promotion must set `applies_discount` and set
 * `discount_trigger` to "Automatic Discount". The other trigger, "Discount Code",
 * is resolved at the cart once the shopper enters the code, so it must not change
 * the price the storefront shows.
 *
 * @see shopify/templates/product.promotion.liquid — serializes the promotion JSON
 * @see documentation/metaobjects/product-promotion.md — field reference
 */

/** The `discount_trigger` value that marks a theme-applied automatic discount. */
const AUTOMATIC_DISCOUNT_TRIGGER = 'automatic discount';

/**
 * Reads the discount fields of a promotion and returns a normalized descriptor.
 *
 * Returns null unless the promotion applies an automatic discount with a usable
 * value. Callers treat null as "render the price unchanged", so a malformed or
 * code-triggered promotion never alters a displayed price.
 *
 * `discount_type` is a closed set: "Percentage Off" reads `percentage_off`, and
 * "Amount Off" reads `amount_off_amount`. The comparison is case-insensitive, but an
 * unrecognized value returns null rather than guessing which amount field to read.
 *
 * @param {Object} promotion - Promotion object from the promotion endpoint
 * @returns {{type: 'percent'|'amount', percentOff?: number, amountOffCents?: number}|null}
 */
export function getPromotionDiscount(promotion) {
  const fields = promotion?.fields;
  if (!fields) return null;
  if (fields.applies_discount !== true) return null;

  const trigger = String(fields.discount_trigger ?? '').trim().toLowerCase();
  if (trigger !== AUTOMATIC_DISCOUNT_TRIGGER) return null;

  const discountType = String(fields.discount_type ?? '').trim().toLowerCase();

  if (discountType === 'percentage off') {
    const percentOff = Number(fields.percentage_off);
    if (!Number.isFinite(percentOff) || percentOff <= 0 || percentOff > 100) return null;
    return { type: 'percent', percentOff };
  }

  if (discountType === 'amount off') {
    // `amount_off_amount` is the promotion amount in the store's currency unit.
    // It comes from `amount_off_money | money_amount`, so it is the only amount
    // field with a documented, unambiguous scale. `amount_off` is not used here:
    // it falls back to the money field in Liquid, so its unit is not guaranteed.
    const amountOff = Number(fields.amount_off_amount);
    if (!Number.isFinite(amountOff) || amountOff <= 0) return null;
    return { type: 'amount', amountOffCents: Math.round(amountOff * 100) };
  }

  return null;
}

/**
 * Applies a promotion discount to a price.
 *
 * The discount always comes off the selling price, never the compare-at price.
 * The result is clamped at zero, so an amount-off larger than the price shows
 * as free rather than as a negative number.
 *
 * @param {number} priceInCents - The selling price in cents
 * @param {Object} promotion - Promotion object from the promotion endpoint
 * @returns {number|null} Discounted price in cents, or null when no discount applies
 */
export function getDiscountedPriceInCents(priceInCents, promotion) {
  const price = Number(priceInCents);
  if (!Number.isFinite(price) || price <= 0) return null;

  const discount = getPromotionDiscount(promotion);
  if (!discount) return null;

  const discounted = discount.type === 'percent'
    ? price - (price * (discount.percentOff / 100))
    : price - discount.amountOffCents;

  const rounded = Math.max(0, Math.round(discounted));

  // A discount that does not move the price is not worth a cross-out.
  if (rounded >= price) return null;

  return rounded;
}

/**
 * Applies a promotion's discount preview styling to the discounted price element.
 *
 * A promotion can override how its discounted price looks, through two optional
 * metaobject fields. `discount_preview_text_color` recolors the price, and
 * `discount_preview_font_weight` set to "Bold" emboldens it. Both are left alone
 * when blank, so the price keeps the theme's own styling.
 *
 * Styling lands on the discounted `<ins>` alone. The compare-at `<del>` beside it
 * is a real price rather than part of the promotion, so it keeps its normal
 * treatment.
 *
 * @param {HTMLElement} element - The discounted price element
 * @param {Object} promotion - Promotion object from the promotion endpoint
 */
function applyDiscountPreviewStyles(element, promotion) {
  const fields = promotion?.fields;
  if (!element || !fields) return;

  const textColor = String(fields.discount_preview_text_color ?? '').trim();
  if (textColor) {
    element.style.color = textColor;
  }

  // Only "Bold" changes anything. An empty value and "Normal" both mean
  // "leave the theme's weight alone", so neither writes a style.
  const fontWeight = String(fields.discount_preview_font_weight ?? '').trim().toLowerCase();
  if (fontWeight === 'bold' || fontWeight === 'bolder') {
    element.style.fontWeight = 'bold';
  }
}

/**
 * Builds price markup with any promotion discount already applied.
 *
 * Price markup arrives from the server undiscounted, so applying a discount after it
 * is already on screen paints the pre-discount price first and then rewrites it. That
 * reads as a flash on every variant, swatch, and hydration render. Building the
 * markup off-DOM and discounting it before it is attached means the price paints once,
 * at its final value.
 *
 * @param {string} priceHtml - Server-rendered price markup
 * @param {Object} promotion - Promotion object from the promotion endpoint
 * @param {string} [currencyCode] - ISO 4217 code; defaults to the store currency
 * @returns {DocumentFragment} Fragment holding the finished price markup
 */
export function renderPriceMarkup(priceHtml, promotion, currencyCode) {
  const holder = document.createElement('div');
  holder.innerHTML = priceHtml ?? '';

  // Discount before the markup is attached, so the undiscounted price never paints.
  applyPromotionToPriceElement(holder, promotion, currencyCode);

  const fragment = document.createDocumentFragment();
  fragment.append(...holder.childNodes);
  return fragment;
}

/**
 * Rewrites a rendered price element in place to show its post-discount price.
 *
 * The price markup this theme renders puts the selling price in an `<ins>` and any
 * compare-at price in a `<del>` (see `snippets/product-price-variant.liquid`). This
 * function reads the selling price from the element and replaces it with the
 * discounted price, marked as a discount preview:
 *
 *   before:  <ins>$90</ins>                 after:  <ins class="price-item__discount-preview">$72</ins>
 *   before:  <del>$120</del><ins>$90</ins>  after:  <del>$120</del><ins class="price-item__discount-preview">$72</ins>
 *
 * Any compare-at `<del>` is left untouched, so a variant already on sale keeps its
 * original price crossed out beside the discounted one. The pre-promotion selling
 * price is replaced rather than crossed out, so a card never shows two struck prices.
 *
 * The `.price-item` and its `.price` ancestor are both marked with a
 * `--discount-preview` modifier, so CSS can style the discounted price and hide the
 * product price range, whose product-level bounds carry no discount.
 *
 * Accepts a detached element, which is how callers avoid a visible price flash: build
 * the markup off-DOM, discount it here, then attach it in one mutation so the
 * undiscounted price never paints.
 *
 * The element is marked with a data attribute once rewritten. Price elements are
 * re-rendered from server HTML on every swatch and variant change, so callers
 * re-run this after each render; the marker keeps a repeat call on unchanged DOM
 * from compounding the discount.
 *
 * @param {HTMLElement} priceElement - A container holding rendered price markup
 * @param {Object} promotion - Promotion object from the promotion endpoint
 * @param {string} [currencyCode] - ISO 4217 code; defaults to the store currency
 * @returns {boolean} True when a discount was applied
 */
export function applyPromotionToPriceElement(priceElement, promotion, currencyCode) {
  if (!priceElement) return false;
  if (!getPromotionDiscount(promotion)) return false;

  const currency = currencyCode || window.theme?.currency?.code || 'USD';
  let didApply = false;

  for (const priceItem of priceElement.querySelectorAll('.price-item')) {
    // The price range ("From $x - $y") holds two <ins> values sourced from
    // product-level metafields, which cannot be matched to a promotion-eligible
    // variant. Leave ranges alone rather than advertise a price the cart may not honor.
    if (priceItem.classList.contains('price-item--range')) continue;
    if (priceItem.dataset.promotionApplied === 'true') continue;

    const insElement = priceItem.querySelector('ins');
    if (!insElement) continue;

    const priceInCents = parseMoneyToCents(insElement.textContent);
    if (priceInCents === null) continue;

    const discountedInCents = getDiscountedPriceInCents(priceInCents, promotion);
    if (discountedInCents === null) continue;

    insElement.textContent = formatMoneyFromCurrency(discountedInCents, currency);
    insElement.classList.add('price-item__discount-preview');
    applyDiscountPreviewStyles(insElement, promotion);

    priceItem.classList.add('price-item--discount-preview');
    priceItem.closest('.price')?.classList.add('price--discount-preview');

    priceItem.dataset.promotionApplied = 'true';
    didApply = true;
  }

  return didApply;
}

/**
 * Parses a rendered money string back into cents.
 *
 * Prices reach the client already formatted by Liquid, so a promotion applied on
 * the client has to read the number back out of the rendered string. Handles the
 * thousands separators and currency symbols `money_without_trailing_zeros` emits.
 *
 * @param {string} moneyString - A formatted money string, e.g. "$1,250.50"
 * @returns {number|null} The amount in cents, or null when no amount is found
 */
export function parseMoneyToCents(moneyString) {
  if (!moneyString) return null;

  // Keep digits and separators only, then drop thousands separators so the
  // remaining separator (if any) is the decimal point.
  const numeric = String(moneyString).replace(/[^\d.,]/g, '').trim();
  if (!numeric) return null;

  const lastComma = numeric.lastIndexOf(',');
  const lastPeriod = numeric.lastIndexOf('.');
  const decimalIndex = Math.max(lastComma, lastPeriod);

  let normalized;
  if (decimalIndex === -1) {
    normalized = numeric.replace(/[.,]/g, '');
  } else {
    // Two digits after the final separator means it is a decimal point.
    // Anything else means every separator groups thousands.
    const trailingDigits = numeric.length - decimalIndex - 1;
    normalized = trailingDigits === 2
      ? `${numeric.slice(0, decimalIndex).replace(/[.,]/g, '')}.${numeric.slice(decimalIndex + 1)}`
      : numeric.replace(/[.,]/g, '');
  }

  const amount = Number(normalized);
  if (!Number.isFinite(amount)) return null;

  return Math.round(amount * 100);
}
